/**
 * Radio card role selector for team invitations.
 * Displays ADMIN, AGENT, VIEWER options (NOT OWNER per CONTEXT.md).
 * Each card shows role name and permission description.
 */

import { useTranslation } from 'react-i18next';
import type { TeamRole } from '../../types/company';

// Only roles available for invitation (OWNER excluded)
type InviteRole = Exclude<TeamRole, 'OWNER'>;

interface RoleSelectorProps {
  selectedRole: InviteRole | null;
  onChange: (role: InviteRole) => void;
  disabled?: boolean;
}

const INVITE_ROLES: InviteRole[] = ['ADMIN', 'AGENT', 'VIEWER'];

// Role icon mapping
const ROLE_ICONS: Record<InviteRole, string> = {
  ADMIN: 'ri-shield-user-line',
  AGENT: 'ri-home-gear-line',
  VIEWER: 'ri-eye-line',
};

export default function RoleSelector({
  selectedRole,
  onChange,
  disabled = false,
}: RoleSelectorProps) {
  const { t } = useTranslation('team');

  // Keys are looked up against the `team` namespace (set above via
  // useTranslation('team')) so they're stored without the `team.` prefix.
  // The earlier `team.roleAdmin` style worked while the translation file
  // was double-nested, but after that file was flattened we have to drop
  // the prefix or the lookup falls back to the raw key string.
  const getRoleName = (role: InviteRole): string => {
    const keys: Record<InviteRole, string> = {
      ADMIN: 'roleAdmin',
      AGENT: 'roleAgent',
      VIEWER: 'roleViewer',
    };
    return t(keys[role]);
  };

  const getRoleDescription = (role: InviteRole): string => {
    const keys: Record<InviteRole, string> = {
      ADMIN: 'roleAdminDesc',
      AGENT: 'roleAgentDesc',
      VIEWER: 'roleViewerDesc',
    };
    return t(keys[role]);
  };

  return (
    <div className="space-y-3">
      {INVITE_ROLES.map((role) => {
        const isSelected = selectedRole === role;
        const icon = ROLE_ICONS[role];

        return (
          <button
            key={role}
            type="button"
            onClick={() => !disabled && onChange(role)}
            disabled={disabled}
            className={`
              w-full p-4 rounded-lg border-2 text-left transition-all duration-200
              ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            aria-pressed={isSelected}
          >
            <div className="flex items-start gap-3">
              {/* Radio indicator */}
              <div
                className={`
                  mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                  ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 bg-white'
                  }
                `}
              >
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>

              {/* Role icon */}
              <div
                className={`
                  w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}
                `}
              >
                <i className={`${icon} text-xl`}></i>
              </div>

              {/* Role content */}
              <div className="flex-1 min-w-0">
                <div
                  className={`font-semibold ${
                    isSelected ? 'text-blue-900' : 'text-gray-900'
                  }`}
                >
                  {getRoleName(role)}
                </div>
                <div
                  className={`text-sm mt-0.5 ${
                    isSelected ? 'text-blue-700' : 'text-gray-500'
                  }`}
                >
                  {getRoleDescription(role)}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
